import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { formatClockOrDate, formatDurationBetween } from '../utils/formatters';
import type { OrderResponse } from '../types';

interface Props {
  order: OrderResponse;
  className?: string;
  /** Compact: drops the "Placed" / "Done" / "Cancelled" prefixes for board cards. */
  compact?: boolean;
}

/**
 * One-line timing strip for order cards. Shows when the order was placed and,
 * when terminal, when it finished + the total turnaround.
 *
 *   Active:     [clock] Placed 14:32
 *   Completed:  [clock] Placed 14:32 · Done 15:05 (23m)
 *   Cancelled:  [clock] Placed 14:32 · Cancelled 14:35 (3m)
 */
export function OrderTimingLine({ order, className, compact }: Props) {
  if (!order.createdAt) return null;

  const terminalIso = terminalTimestamp(order);
  const placed = formatClockOrDate(order.createdAt);
  const terminal = terminalIso ? formatClockOrDate(terminalIso) : null;
  const duration = terminalIso ? formatDurationBetween(order.createdAt, terminalIso) : null;

  const terminalTone =
    order.orderStatus === 'COMPLETED'
      ? 'text-success'
      : order.orderStatus === 'CANCELLED'
        ? 'text-destructive'
        : null;
  const TerminalIcon =
    order.orderStatus === 'COMPLETED'
      ? CheckCircle2
      : order.orderStatus === 'CANCELLED'
        ? XCircle
        : null;
  const terminalLabel =
    order.orderStatus === 'COMPLETED'
      ? 'Done'
      : order.orderStatus === 'CANCELLED'
        ? 'Cancelled'
        : null;

  return (
    <p
      className={cn(
        'flex items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums flex-wrap',
        className,
      )}
    >
      <Clock className="w-3 h-3 shrink-0" aria-hidden />
      <span>
        {compact ? '' : 'Placed '}
        <span className="font-medium text-foreground">{placed}</span>
      </span>
      {terminal && terminalLabel && TerminalIcon && (
        <>
          <span className="text-muted-foreground/60" aria-hidden>·</span>
          <span className={cn('inline-flex items-center gap-1', terminalTone)}>
            <TerminalIcon className="w-3 h-3 shrink-0" aria-hidden />
            {compact ? '' : `${terminalLabel} `}
            <span className="font-medium">{terminal}</span>
          </span>
          {duration && (
            <span className="text-muted-foreground/70">({duration})</span>
          )}
        </>
      )}
    </p>
  );
}

function terminalTimestamp(order: OrderResponse): string | null {
  if (order.orderStatus === 'COMPLETED') {
    return order.completedAt ?? order.updatedAt ?? null;
  }
  if (order.orderStatus === 'CANCELLED') {
    return order.cancelledAt ?? order.updatedAt ?? null;
  }
  return null;
}
